/**
 * What the scene tree shows at the top (SCENE_ROOT_SPEC D8, sb#334).
 *
 * The Inspector's "Nodes" section enumerates `scene.rootNodes`, so without this
 * the tree reads `Scene (engine root) → <document root> → [content]`: the first
 * entry is the runtime MOUNT POINT, which is not a node of the file at all. It
 * is never shown, never selected, and never a drop target — the tree starts at
 * the **document root**.
 *
 * Everything here is pure and takes Babylon `Node`s, so it can be unit-tested
 * against a live `TraceScene` without mounting an Inspector. The wiring that
 * feeds these answers to the Inspector's Nodes section lives next door in
 * `scene-tree-service.ts`.
 */

import type { Node } from '@babylonjs/core/node';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { documentRoot } from 'trace-viewer/editor';

/** The engine mount point's transform, or null when the session has no scene. */
export function engineRootNode(traceScene: TraceScene | null | undefined): TransformNode | null {
    return traceScene?.getSceneRoot()?.transform ?? null;
}

/** The document root's transform — what the tree starts at (D8). */
export function documentRootNode(traceScene: TraceScene | null | undefined): TransformNode | null {
    return documentRoot(traceScene)?.transform ?? null;
}

/**
 * The roots the tree actually shows, given the roots the Inspector found.
 *
 * The engine root is replaced by its CHILDREN — the document root, plus any
 * object that is momentarily top-level (an import not yet re-parented, a drag
 * the D8 policy is about to heal). Expanding rather than hard-coding the
 * document root is what keeps such an object visible instead of stranding it in
 * a tree that cannot show it.
 *
 * Order is preserved and duplicates are dropped; a session with no scene, or
 * one whose scene never started, is returned unchanged.
 */
export function visibleTreeRoots(
    entities: readonly Node[],
    traceScene: TraceScene | null | undefined,
): Node[] {
    const engineRoot = engineRootNode(traceScene);
    if (!engineRoot) return [...entities];

    const seen = new Set<Node>();
    const roots: Node[] = [];
    const push = (node: Node): void => {
        if (node === engineRoot || seen.has(node)) return;
        seen.add(node);
        roots.push(node);
    };

    // `getChildTransforms()` rather than the raw node's children: it is
    // transparent to an animation root, so a mount point that ever grows one
    // does not put `Scene__animRoot` where the document root should be.
    const children = () => traceScene?.getSceneRoot()?.getChildTransforms() ?? [];

    for (const entity of entities) {
        if (entity === engineRoot) {
            for (const child of children()) push(child);
        } else {
            push(entity);
        }
    }
    return roots;
}

/**
 * Whether `node` is one of the two structural roots — the engine mount point or
 * the document root. Neither is dragged anywhere: the first is not a node of
 * the file, and moving the second would put the document inside itself.
 */
export function isStructuralTreeNode(
    node: Node | null | undefined,
    traceScene: TraceScene | null | undefined,
): boolean {
    if (!node) return false;
    return node === engineRootNode(traceScene) || node === documentRootNode(traceScene);
}

/**
 * Where a drop lands, or null when it is refused.
 *
 * `target === null` is the section root, which with the engine root hidden can
 * only mean "at the top of the document" — so it resolves to the document root
 * rather than to `parent = null`, which would take the object out of the
 * document entirely. A drop onto the engine root itself is refused for the same
 * reason (it cannot be reached in the tree, but a stale drag could still name
 * it).
 */
export function resolveTreeDropTarget(
    dragged: Node,
    target: Node | null,
    traceScene: TraceScene | null | undefined,
): Node | null {
    if (isStructuralTreeNode(dragged, traceScene)) return null;
    const engineRoot = engineRootNode(traceScene);
    if (target === engineRoot && engineRoot !== null) return null;

    const resolved = target ?? documentRootNode(traceScene);
    if (!resolved) return null;
    if (resolved === dragged) return null;
    if (dragged.parent === resolved) return null;
    return resolved;
}

/** The objects a user-facing listing starts from: the document root's own. */
export function treeRootObjects(traceScene: TraceScene | null | undefined): SceneObject[] {
    const root = documentRoot(traceScene);
    if (root) return [root];
    const engineRoot = traceScene?.getSceneRoot();
    return engineRoot ? engineRoot.getChildren() : [];
}
